import * as THREE from 'three';
import Trail from './Trail.js';

/**
 * Firework类 - 单个烟花的完整生命周期
 * 
 * 生命周期阶段:
 * 1. Launch Phase: 上升阶段，显示发射轨迹
 * 2. Explosion Phase: 爆炸阶段，创建粒子
 * 3. Particle Phase: 粒子扩散和下落阶段
 * 4. Cleanup Phase: 粒子消失，清理资源
 * 
 * 验证需求: Requirements 2.1, 5.1
 */
class Firework {
  constructor(config) {
    // 配置参数
    this.config = {
      launchPosition: config.launchPosition || new THREE.Vector3(0, 0, 0),
      launchAngle: config.launchAngle || 90,
      launchSpeed: config.launchSpeed || 20,
      explosionHeight: config.explosionHeight || 20,
      explosionPattern: config.explosionPattern || 'sphere',
      particleCount: config.particleCount || 1000,
      particleSpeed: config.particleSpeed || 8,
      innerColor: config.innerColor || new THREE.Color(1, 0.5, 0),
      outerColor: config.outerColor || new THREE.Color(1, 1, 0),
      hasTrail: config.hasTrail !== undefined ? config.hasTrail : true,
      trailLength: config.trailLength || 20
    };

    // 引擎引用
    this.scene = config.scene;
    this.particleEngine = config.particleEngine;

    // 当前状态
    this.phase = 'idle'; // 'idle', 'launching', 'exploding', 'finished'
    this.position = this.config.launchPosition.clone();
    this.velocity = new THREE.Vector3(0, 0, 0);
    this.age = 0;
    this.launchDuration = 1.2; // 上升时间（秒）

    // 发射轨迹
    this.launchTrail = null;
    
    // 是否已爆炸
    this.hasExploded = false;
  }

  /**
   * 发射烟花
   */
  launch() {
    if (this.phase !== 'idle') return;

    this.phase = 'launching';
    this.age = 0;

    // 计算发射速度向量
    const angleRad = THREE.MathUtils.degToRad(this.config.launchAngle);
    
    // 随机方向（水平方向）
    const horizontalAngle = Math.random() * Math.PI * 2;
    
    // 计算速度分量
    const horizontalSpeed = Math.sin(angleRad) * this.config.launchSpeed;
    const verticalSpeed = Math.cos(angleRad) * this.config.launchSpeed;
    
    this.velocity.set(
      Math.cos(horizontalAngle) * horizontalSpeed,
      verticalSpeed,
      Math.sin(horizontalAngle) * horizontalSpeed
    );

    // 创建发射轨迹
    if (this.config.hasTrail && this.scene) {
      this.launchTrail = new Trail(
        this.config.trailLength,
        this.config.innerColor,
        0.8
      );
      this.scene.add(this.launchTrail.getMesh());
    }
  }

  /**
   * 爆炸
   */
  explode() {
    if (this.hasExploded) return;

    this.hasExploded = true;
    this.phase = 'exploding';

    // 创建粒子爆炸
    if (this.particleEngine) {
      this.particleEngine.createExplosion(
        this.position,
        this.config.explosionPattern,
        {
          inner: this.config.innerColor,
          outer: this.config.outerColor
        },
        this.config.particleCount
      );
    }

    // 清理发射轨迹
    if (this.launchTrail) {
      this.scene.remove(this.launchTrail.getMesh());
      this.launchTrail.dispose();
      this.launchTrail = null;
    }
  }

  /**
   * 更新烟花状态
   * @param {number} deltaTime - 时间增量（秒）
   */
  update(deltaTime) {
    if (this.phase === 'idle' || this.phase === 'finished') {
      return;
    }

    this.age += deltaTime;

    if (this.phase === 'launching') {
      // 更新位置
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
      this.position.z += this.velocity.z * deltaTime;

      // 应用重力
      this.velocity.y -= 9.8 * deltaTime;

      // 更新轨迹
      if (this.launchTrail) {
        this.launchTrail.update(this.position, this.velocity.length());
      }

      // 检查是否到达爆炸高度或时间
      const shouldExplode = 
        this.position.y >= this.config.explosionHeight ||
        this.age >= this.launchDuration ||
        this.velocity.y <= 0; // 速度向下时也爆炸

      if (shouldExplode) {
        this.explode();
      }
    } else if (this.phase === 'exploding') {
      // 爆炸后等待一小段时间再标记为完成
      if (this.age > this.launchDuration + 0.5) {
        this.phase = 'finished';
      }
    }
  }

  /**
   * 检查烟花是否完成
   * @returns {boolean}
   */
  isFinished() {
    return this.phase === 'finished';
  }

  /**
   * 清理资源
   */
  dispose() {
    if (this.launchTrail) {
      if (this.scene) {
        this.scene.remove(this.launchTrail.getMesh());
      }
      this.launchTrail.dispose();
      this.launchTrail = null;
    }

    this.phase = 'finished';
  }

  /**
   * 获取当前位置
   * @returns {THREE.Vector3}
   */
  getPosition() {
    return this.position.clone();
  }

  /**
   * 获取当前阶段
   * @returns {string}
   */
  getPhase() {
    return this.phase;
  }
}

export default Firework;
